import React from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import DialogWindow from '@/common/components/_misc/DialogWindow';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  ProjectInviteInput,
  useInviteToProjectMutation,
} from '@/generated/graphql';
import { useNotification } from '@/common/components/notification/NotificationContext';
import { ProjectMemberRole, ProjectInviteStatus } from '@/common/models/enums';
import { getProjectMemberRoleDescription } from '@/common/utils/statusUtils';

interface InviteMemberDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  currentUserEmail: string | null;
  invitableRoles: ProjectMemberRole[];
  existingInvites: Array<{ email: string; status: string }>;
  existingMembers: Array<{ userEmail: string }>;
  onSuccess: () => void;
}

export const InviteMemberDialog: React.FC<InviteMemberDialogProps> = ({
  open,
  onClose,
  projectId,
  currentUserEmail,
  invitableRoles,
  existingInvites,
  existingMembers,
  onSuccess,
}) => {
  const { showSuccess, showError } = useNotification();
  const [inviteToProject, { loading: inviteLoading }] =
    useInviteToProjectMutation();

  const defaultRole =
    invitableRoles.find((r) => r === ProjectMemberRole.COLLABORATOR) ??
    invitableRoles[0] ??
    ProjectMemberRole.COLLABORATOR;

  const checkValidInvite = (email: string): string | null => {
    if (!email) return null;

    const normalizedInviteEmail = email.trim().toLowerCase();

    if (normalizedInviteEmail === currentUserEmail?.toLowerCase()) {
      return "You can't invite yourself to a project";
    } else if (
      existingInvites.some(
        (invite) =>
          invite.email.trim().toLowerCase() === normalizedInviteEmail &&
          invite.status === ProjectInviteStatus.PENDING
      )
    ) {
      return 'This user has already been invited to the project';
    } else if (
      existingMembers.some(
        (member) =>
          member.userEmail.trim().toLowerCase() === normalizedInviteEmail
      )
    ) {
      return 'This user has already joined the project';
    } else {
      return null;
    }
  };

  const inviteValidationSchema = Yup.object({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required')
      .test('not-valid-invite', 'Invalid invite.', function (value: string) {
        const message = checkValidInvite(value || '');
        if (message) {
          return this.createError({ message: message });
        }
        return true;
      }),
    role: Yup.string()
      .required('Role is required')
      .oneOf(invitableRoles, 'Invalid role selected'),
  });

  const handleInviteUser = async (values: { email: string; role: string }) => {
    try {
      const result = await inviteToProject({
        variables: {
          data: {
            projectId,
            email: values.email,
            role: values.role,
          } as ProjectInviteInput,
        },
        errorPolicy: 'all',
      });

      if (result?.errors?.length) {
        switch (result.errors[0].message) {
          case 'Invitation already sent to this email for this project':
            showError('This user is already invited to this project');
            break;
          case 'InvalidEmail':
            showError('Invalid email address provided');
            break;
          default:
            showError('Failed to send invitation');
            break;
        }
        return;
      }

      showSuccess(`Invitation sent to ${values.email}`);
      onClose();
      onSuccess();
    } catch (error) {
      console.error('Error inviting user:', error);
      const message =
        error instanceof Error ? error.message : 'Failed to send invitation';
      showError(message);
    }
  };

  if (invitableRoles.length === 0) {
    return null;
  }

  return (
    <DialogWindow
      open={open}
      onClose={onClose}
      title="Invite Member to Project"
      maxWidth="sm"
      fullWidth
    >
      <Formik
        key={invitableRoles.join(',')}
        initialValues={{
          email: '',
          role: defaultRole,
        }}
        validationSchema={inviteValidationSchema}
        onSubmit={handleInviteUser}
        validateOnMount
        validateOnChange
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          isValid,
        }) => (
          <Form onSubmit={handleSubmit}>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={3}>
                <TextField
                  fullWidth
                  name="email"
                  label="Email Address"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />

                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="role"
                    value={values.role}
                    onChange={handleChange}
                    label="Role"
                  >
                    {invitableRoles.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Alert severity="info">
                  <Typography variant="body2" component="div">
                    {invitableRoles.map((role) => {
                      const description = getProjectMemberRoleDescription(role);
                      if (!description) return null;

                      return (
                        <React.Fragment key={role}>
                          <strong>{role}:</strong> {description}
                          <br />
                        </React.Fragment>
                      );
                    })}
                  </Typography>
                </Alert>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                name="sendInvitation"
                title="Send Invitation"
                disabled={isSubmitting || inviteLoading || !isValid}
                startIcon={
                  inviteLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <PersonAddIcon />
                  )
                }
              >
                Send Invitation
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </DialogWindow>
  );
};
